/**
 * Migration batches (ADR 0055 amendment 2026-10-22): what the ops Migration
 * page calls. A batch of live Vodia phones is staged (preview, holds, backup
 * of the extension's current parameter), pointed (Vodia's `snom-general`
 * written per extension — the change-set apply), pushed (check-sync),
 * observed from edge check-ins and registrations, and rolled back (parameter
 * restored or cleared; the phone handed Vodia's own entry URL). Reads need
 * `provisioning.view`; every state change needs `migration.manage`.
 */

import { apiFetch } from "./client";

export type MigrationBatchStatus = "draft" | "pointed" | "failed" | "rolled_back" | "cancelled";

export type MigrationItemState =
  | "planned"
  | "held"
  | "pointed"
  | "point_failed"
  | "pushed"
  | "push_failed"
  | "arrived"
  | "registered"
  | "rolling_back"
  | "rolled_back";

export interface MigrationCandidate {
  device_id: number;
  mac: string;
  label: string | null;
  model: string | null;
  node_name: string | null;
  domain: string | null;
  extension: string | null;
  /** null = the phone can go. */
  hold_reason: string | null;
  why: string | null;
}

export interface MigrationItem {
  id: number;
  device_id: number;
  mac: string;
  label: string | null;
  model: string | null;
  node_name: string | null;
  domain: string | null;
  extension: string | null;
  state: MigrationItemState;
  hold_reason: string | null;
  /** The extension's `snom-general` before the point; null = none set. */
  prior_value: string | null;
  /** Vodia's entry URL the rollback render hands the phone. */
  rollback_url: string | null;
  detail: Record<string, unknown> | null;
  pointed_at: string | null;
  pushed_at: string | null;
  arrived_at: string | null;
  registered_at: string | null;
  rolled_back_at: string | null;
}

export interface MigrationBatch {
  id: number;
  name: string;
  status: MigrationBatchStatus;
  /** The parameter text every phone in the batch receives. */
  text: string;
  note: string | null;
  created_by: string | null;
  created_at: string | null;
  pointed_by: string | null;
  pointed_at: string | null;
  rolled_back_by: string | null;
  rolled_back_at: string | null;
  items: MigrationItem[];
  counts: Partial<Record<MigrationItemState, number>>;
}

/** The list form carries no items. */
export type MigrationBatchSummary = Omit<MigrationBatch, "items">;

export function getMigrationText(): Promise<{ text: string }> {
  return apiFetch("/api/migration/text");
}

export function getMigrationCandidates(): Promise<{ items: MigrationCandidate[]; total: number }> {
  return apiFetch("/api/migration/candidates");
}

export function getMigrationBatches(): Promise<{ items: MigrationBatchSummary[]; total: number }> {
  return apiFetch("/api/migration/batches");
}

/** One batch; the read also advances states from the latest check-ins. */
export function getMigrationBatch(id: number): Promise<MigrationBatch> {
  return apiFetch(`/api/migration/batches/${id}`);
}

export function stageMigrationBatch(
  name: string,
  deviceIds: number[],
  note?: string,
): Promise<MigrationBatch> {
  return apiFetch("/api/migration/batches", {
    method: "POST",
    body: { name, device_ids: deviceIds, note: note || null },
  });
}

export function pointMigrationBatch(id: number): Promise<MigrationBatch> {
  return apiFetch(`/api/migration/batches/${id}/point`, { method: "POST" });
}

export function pushMigrationBatch(id: number): Promise<MigrationBatch> {
  return apiFetch(`/api/migration/batches/${id}/push`, { method: "POST" });
}

export function rollbackMigrationBatch(id: number): Promise<MigrationBatch> {
  return apiFetch(`/api/migration/batches/${id}/rollback`, { method: "POST" });
}

export function cancelMigrationBatch(id: number): Promise<MigrationBatch> {
  return apiFetch(`/api/migration/batches/${id}/cancel`, { method: "POST" });
}
